import React, { useState } from 'react';
import { User, Copy, Trash2, RefreshCw } from 'lucide-react';

const SocialBioGeneratorTool = () => {
  const [platform, setPlatform] = useState('twitter');
  const [profession, setProfession] = useState('');
  const [interests, setInterests] = useState('');
  const [tone, setTone] = useState('professional');
  const [bios, setBios] = useState([]);
  const [copied, setCopied] = useState('');

  const platforms = {
    twitter: { name: 'Twitter/X', limit: 160 },
    instagram: { name: 'Instagram', limit: 150 },
    linkedin: { name: 'LinkedIn', limit: 2000 },
    tiktok: { name: 'TikTok', limit: 80 },
    youtube: { name: 'YouTube', limit: 1000 },
    github: { name: 'GitHub', limit: 160 }
  };

  const templates = {
    professional: [
      '{profession} | {interests} enthusiast | Building things that matter',
      '{profession} helping others succeed | {interests} | Let\'s connect',
      'Passionate {profession} | Sharing insights on {interests}',
      '{profession} by day | {interests} explorer by heart',
    ],
    casual: [
      'Just a {profession} who loves {interests}',
      '{profession} vibes | {interests} obsessed | Life is good',
      'Making {interests} cool since forever | {profession}',
      'Your friendly neighborhood {profession} | {interests} fan',
    ],
    creative: [
      '{profession} turning ideas into reality | {interests} dreamer',
      'Creating magic as a {profession} | {interests} believer',
      '{profession} with a passion for {interests} | Join the journey',
      'Where {profession} meets {interests} | Follow along',
    ],
    minimal: [
      '{profession} | {interests}',
      '{profession}. {interests}. That\'s it.',
      'Doing {profession} things | {interests}',
      '{profession} • {interests}',
    ]
  };

  const emojis = {
    professional: ['💼', '🎯', '📈', '✨', '🚀'],
    casual: ['😊', '🔥', '💪', '🌟', '👋'],
    creative: ['🎨', '✨', '💡', '🌈', '🦋'],
    minimal: ['•', '|', '→', '—', '·']
  };

  const generateBios = () => {
    const prof = profession.trim() || 'creator';
    const ints = interests.trim() || 'innovation';
    const limit = platforms[platform].limit;
    
    const toneTemplates = templates[tone];
    const toneEmojis = emojis[tone];
    
    const generated = toneTemplates.map(template => {
      let bio = template
        .replace('{profession}', prof)
        .replace('{interests}', ints);
      
      // Add emoji if space allows
      if (tone !== 'minimal' && bio.length + 2 < limit) {
        const emoji = toneEmojis[Math.floor(Math.random() * toneEmojis.length)];
        bio = `${emoji} ${bio}`;
      }
      
      // Truncate if needed
      if (bio.length > limit) {
        bio = bio.substring(0, limit - 3) + '...';
      }
      
      return {
        text: bio,
        length: bio.length,
        limit
      };
    });
    
    setBios(generated);
  };

  const copyBio = (bio, index) => {
    navigator.clipboard.writeText(bio);
    setCopied(index);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Platform</label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            {Object.entries(platforms).map(([key, info]) => (
              <option key={key} value={key}>{info.name} ({info.limit} chars)</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Tone</label>
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="professional">Professional</option>
            <option value="casual">Casual</option>
            <option value="creative">Creative</option>
            <option value="minimal">Minimal</option>
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Profession / Role</label>
          <input
            type="text"
            value={profession}
            onChange={(e) => setProfession(e.target.value)}
            placeholder="e.g., Developer, Designer, Writer"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Interests / Niche</label>
          <input
            type="text"
            value={interests}
            onChange={(e) => setInterests(e.target.value)}
            placeholder="e.g., AI, Photography, Fitness"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={generateBios}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
          <User size={18} />
          Generate Bios
        </button>
        <button
          onClick={generateBios}
          disabled={bios.length === 0}
          className="flex items-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {bios.length > 0 && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-300">Generated Bios</label>
          {bios.map((bio, i) => (
            <div key={i} className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-start justify-between gap-4">
                <p className="text-white flex-1">{bio.text}</p>
                <button
                  onClick={() => copyBio(bio.text, i)}
                  className={`p-2 rounded transition-colors flex-shrink-0 ${
                    copied === i ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={16} />
                </button>
              </div>
              <div className="mt-2 text-xs">
                <span className={bio.length <= bio.limit ? 'text-green-400' : 'text-red-400'}>
                  {bio.length}/{bio.limit} characters
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Bio Writing Tips</h3>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• Keep it concise and easy to read</li>
          <li>• Include keywords related to your niche</li>
          <li>• Show personality that matches your content</li>
          <li>• Add a call-to-action if space allows</li>
          <li>• Update regularly to stay relevant</li>
        </ul>
      </div>

      <button
        onClick={() => setBios([])}
        disabled={bios.length === 0}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default SocialBioGeneratorTool;
